import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useMutation } from '@tanstack/react-query';
import { runQuery, type QueryResult } from '../../api/mysql';
import type { MySQLDatabase } from '../../api/mysql';

const LS_KEY = 'mysql-query-history';

interface HistoryEntry {
  sql: string;
  db: string;
  ms: number;
  rows: number;
  ts: string;
}

function cellStr(v: unknown): string {
  if (v === null || v === undefined) return 'NULL';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

interface Props {
  defaultDb: string | null;
  databases: MySQLDatabase[];
}

export function QueryEditorTab({ defaultDb, databases }: Props) {
  const [sql, setSql] = useState('');
  const [db, setDb] = useState(defaultDb ?? '');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '[]') as HistoryEntry[]; } catch { return []; }
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (defaultDb && !db) setDb(defaultDb);
  }, [defaultDb, db]);

  const mutation = useMutation({
    mutationFn: () => runQuery(db, sql),
    onSuccess: (data) => {
      setResult(data);
      if (!data.error) {
        const entry: HistoryEntry = { sql: sql.trim(), db, ms: data.executionMs, rows: data.rowCount, ts: new Date().toLocaleTimeString() };
        const next = [entry, ...history].slice(0, 50);
        setHistory(next);
        localStorage.setItem(LS_KEY, JSON.stringify(next));
      }
    },
  });

  function handleKeyDown(e: React.KeyboardEvent) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (sql.trim() && db) mutation.mutate();
    }
  }

  function loadHistory(entry: HistoryEntry) {
    setSql(entry.sql);
    setDb(entry.db);
    setHistoryOpen(false);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Editor */}
        <Stack spacing={1} sx={{ p: 2, flexShrink: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Database</InputLabel>
              <Select value={db} label="Database" onChange={(e) => setDb(e.target.value)}>
                {databases.map((d) => (
                  <MenuItem key={d.name} value={d.name}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Toggle history">
              <IconButton size="small" onClick={() => setHistoryOpen((o) => !o)} color={historyOpen ? 'primary' : 'default'}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={mutation.isPending ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              onClick={() => mutation.mutate()}
              disabled={!sql.trim() || !db || mutation.isPending}
            >
              Run
            </Button>
          </Stack>
          <Box
            component="textarea"
            ref={textareaRef}
            value={sql}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSql(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="SELECT * FROM table LIMIT 100;  (Ctrl+Enter to run)"
            spellCheck={false}
            sx={{
              width: '100%',
              minHeight: 140,
              resize: 'vertical',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              bgcolor: 'background.default',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              outline: 'none',
              '&:focus': { borderColor: 'primary.main' },
            }}
          />
        </Stack>

        <Divider />

        {/* Results */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {mutation.isPending && <CircularProgress size={24} />}

          {result && !mutation.isPending && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                {result.error ? (
                  <Chip label="Error" size="small" color="error" />
                ) : (
                  <Chip label="OK" size="small" color="success" />
                )}
                <Typography variant="caption" color="text.secondary">
                  {result.rowCount} row{result.rowCount !== 1 ? 's' : ''}
                  {result.affectedRows != null ? ` · ${result.affectedRows} affected` : ''}
                  {' · '}{result.executionMs}ms
                </Typography>
              </Stack>

              {result.error && <Alert severity="error" sx={{ mb: 1 }}>{result.error}</Alert>}

              {result.columns.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {result.columns.map((col) => (
                          <TableCell key={col} sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.rows.map((row, i) => (
                        <TableRow key={i} hover>
                          {result.columns.map((col) => (
                            <TableCell key={col} sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row[col] === null ? <em style={{ color: '#666' }}>NULL</em> : cellStr(row[col])}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {!result && !mutation.isPending && (
            <Typography color="text.secondary" variant="body2">
              Results will appear here. Press Ctrl+Enter to run.
            </Typography>
          )}
        </Box>
      </Box>

      {/* History panel */}
      <Collapse in={historyOpen} orientation="horizontal">
        <Box sx={{ width: 300, borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing="0.07em">
              History
            </Typography>
            <Tooltip title="Clear history">
              <IconButton size="small" onClick={() => { setHistory([]); localStorage.removeItem(LS_KEY); }}>
                <DeleteSweepIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List dense disablePadding>
              {history.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 1.5, display: 'block' }}>
                  No history yet
                </Typography>
              )}
              {history.map((entry, i) => (
                <ListItemButton key={i} onClick={() => loadHistory(entry)} divider sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.75 }}>
                  <ListItemText
                    primary={entry.sql.length > 80 ? entry.sql.slice(0, 80) + '…' : entry.sql}
                    primaryTypographyProps={{ variant: 'caption', fontFamily: 'monospace', noWrap: false, sx: { wordBreak: 'break-all' } }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {entry.db} · {entry.rows} rows · {entry.ms}ms · {entry.ts}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
